import React, { useState } from 'react';
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff, MdAdminPanelSettings, MdSecurity } from 'react-icons/md';

// Admin login — HCEN_Admin_API on HCEN_Website_Gateway (fg0d020oeg)
const ADMIN_API = process.env.REACT_APP_WEBSITE_API_URL
  || 'https://fg0d020oeg.execute-api.ap-southeast-2.amazonaws.com';

export default function AdminLogin({ onLogin }) {
  const [form,    setForm]    = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Multi-step: 'password' → optional 'mfa' / 'newpass'
  const [step,    setStep]    = useState('password');
  const [session, setSession] = useState('');
  const [code,    setCode]    = useState('');
  const [newPass, setNewPass] = useState('');

  const post = async (path, payload) => {
    const res  = await fetch(`${ADMIN_API}${path}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });
    let data; try { data = await res.json(); } catch { data = {}; }
    return { res, data };
  };

  // Store token + admin, then hand off to the app shell.
  const finish = (data) => {
    localStorage.setItem('hcen_admin_token', data.token);
    if (data.refreshToken) {
      localStorage.setItem('hcen_admin_refresh_token', data.refreshToken);
    } else {
      localStorage.removeItem('hcen_admin_refresh_token');
    }
    // Persist email separately so Cognito refresh always has USERNAME for SECRET_HASH.
    const email = String(data.admin?.email || form.email || '').toLowerCase().trim();
    if (email) localStorage.setItem('hcen_admin_email', email);
    localStorage.setItem('hcen_admin', JSON.stringify(data.admin));
    onLogin({ ...data.admin, token: data.token });
  };

  // A response is either { success:true, token, admin } or a challenge.
  const handleResult = (res, data) => {
    if (data.success && data.token) { finish(data); return true; }
    if (data.challenge === 'SOFTWARE_TOKEN_MFA')   { setSession(data.session); setStep('mfa');     return true; }
    if (data.challenge === 'NEW_PASSWORD_REQUIRED'){ setSession(data.session); setStep('newpass'); return true; }
    setError(data.error || 'Invalid credentials');
    return false;
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { res, data } = await post('/admin/login', { email: form.email, password: form.password });
      handleResult(res, data);
    } catch { setError('Connection error. Check internet.'); }
    setLoading(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { res, data } = await post('/admin/login/mfa', { email: form.email, code: code.trim(), session });
      handleResult(res, data);
    } catch { setError('Connection error. Check internet.'); }
    setLoading(false);
  };

  const submitNewPass = async (e) => {
    e.preventDefault();
    setError('');
    if (newPass.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { res, data } = await post('/admin/login/new-password', { email: form.email, newPassword: newPass, session });
      handleResult(res, data);
    } catch { setError('Connection error. Check internet.'); }
    setLoading(false);
  };

  const errBox = error && (
    <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#f87171', fontSize: 13, marginBottom: 14 }}>
      ⚠ {error}
    </div>
  );

  return (
    <div className="auth-page" style={{ fontFamily: 'Outfit,Inter,sans-serif' }}>
      <div className="auth-page__glow-teal" />
      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>

        {/* Branding */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img
            src="/hcen_logo.png"
            alt="HCEN"
            style={{
              width: 82, height: 82, borderRadius: 22,
              objectFit: 'cover', marginBottom: 14,
              boxShadow: '0 8px 40px rgba(245,158,11,0.35)',
            }}
          />
          <h1 style={{
            fontSize: 30, fontWeight: 800,
            background: 'linear-gradient(135deg,var(--text-primary),var(--color-primary))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            marginBottom: 4,
          }}>HCEN Admin</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Healthcare Emergency Network — Owner Control Panel
          </p>
        </div>

        {/* Card */}
        <div className="glass-card" style={{
          border: '1px solid var(--border-color)',
          borderRadius: 18, padding: '32px 28px',
          boxShadow: 'var(--shadow-card-hover)',
        }}>

          {/* ── Step 1: email + password ── */}
          {step === 'password' && (
            <>
              <h2 style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 19, marginBottom: 22, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdAdminPanelSettings style={{ color: 'var(--color-primary)' }} /> Admin Sign In
              </h2>
              <form onSubmit={submitPassword}>
                <div className="form-group">
                  <label className="form-label" htmlFor="admin-email">Admin email</label>
                  <div className="auth-input-wrap">
                    <MdEmail className="auth-input-icon" aria-hidden />
                    <input id="admin-email" type="email" required value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@hcen.in" className="form-input" autoComplete="email" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="admin-password">Admin password</label>
                  <div className="auth-input-wrap">
                    <MdLock className="auth-input-icon" aria-hidden />
                    <input id="admin-password" type={showPwd ? 'text' : 'password'} required value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="Enter your password" className="form-input has-toggle" autoComplete="current-password" />
                    <button type="button" className="auth-input-toggle" onClick={() => setShowPwd(p => !p)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}>
                      {showPwd ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                </div>
                {errBox}
                <button type="submit" disabled={loading} className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}>
                  <MdAdminPanelSettings style={{ fontSize: 20 }} />
                  {loading ? 'Signing in...' : 'Sign In as Admin'}
                </button>
              </form>
            </>
          )}

          {/* ── Step 2a: MFA code ── */}
          {step === 'mfa' && (
            <>
              <h2 style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 19, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdSecurity style={{ color: 'var(--color-primary)' }} /> Two-Factor Verification
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
                Enter the 6-digit code from your authenticator app for <strong>{form.email}</strong>.
              </p>
              <form onSubmit={submitMfa}>
                <div className="form-group">
                  <label className="form-label" htmlFor="admin-mfa">Authenticator code</label>
                  <input id="admin-mfa" type="text" inputMode="numeric" autoComplete="one-time-code" required
                    value={code} onChange={e => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                    placeholder="123456" className="form-input" style={{ letterSpacing: 6, fontSize: 20, textAlign: 'center' }} autoFocus />
                </div>
                {errBox}
                <button type="submit" disabled={loading || code.length < 6} className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}>
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </button>
              </form>
              <button onClick={() => { setStep('password'); setError(''); setCode(''); }}
                style={{ marginTop: 14, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, width: '100%' }}>
                ← Back
              </button>
            </>
          )}

          {/* ── Step 2b: forced first-login new password ── */}
          {step === 'newpass' && (
            <>
              <h2 style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 19, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdLock style={{ color: 'var(--color-primary)' }} /> Set a New Password
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
                First sign-in for <strong>{form.email}</strong>. Choose a new password (min 8 characters).
              </p>
              <form onSubmit={submitNewPass}>
                <div className="form-group">
                  <label className="form-label" htmlFor="admin-newpass">New password</label>
                  <div className="auth-input-wrap">
                    <MdLock className="auth-input-icon" aria-hidden />
                    <input id="admin-newpass" type={showPwd ? 'text' : 'password'} required value={newPass}
                      onChange={e => setNewPass(e.target.value)} placeholder="New password"
                      className="form-input has-toggle" autoComplete="new-password" autoFocus />
                    <button type="button" className="auth-input-toggle" onClick={() => setShowPwd(p => !p)}>
                      {showPwd ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                </div>
                {errBox}
                <button type="submit" disabled={loading} className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}>
                  {loading ? 'Saving...' : 'Set Password & Sign In'}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: 'var(--text-muted)' }}>
          Hospital staff? <a href="/login" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600 }}>Go to Hospital Login →</a>
        </p>
      </div>
    </div>
  );
}
